// `GET /v1/agents/history` (#5208) — who last changed an agent's model or switch, and
// a short timeline. The entry and its parse are `value-objects/agent-history.ts`;
// this is its published projection.
//
// STRICT, every level. The read is `read:billing`; a mode's `code_tools` stay behind
// `write:prompts` (epic #5200 D11), so a strict schema makes a leaked `code_tools`
// (or any other field) a parse failure at the seam rather than a field nobody noticed.

import { z } from 'zod';
import { AGENT_MODE_KEYS, type AgentModeKey, AgentModeScopeSchema } from '../entities/agent-mode';
import {
  AGENT_CONFIG_EVENTS,
  type AgentConfigEvent,
  type AgentHistoryEntry,
  CredentialViaSchema,
} from '../value-objects/agent-history';
import { defineResource } from './resource';

const PinWireSchema = z
  .object({
    key_label: z.string().nullable(),
    provider: z.string().nullable(),
    model: z.string().nullable(),
    reasoning_effort: z.string().nullable(),
  })
  .strict();

export const AgentHistoryEventWireSchema = z
  .object({
    id: z.string().describe('The audit event id.'),
    event: z
      .enum(AGENT_CONFIG_EVENTS as unknown as [AgentConfigEvent, ...AgentConfigEvent[]])
      .describe('What changed: a model pin set or cleared, or a mode switch set or reset.'),
    at_iso: z.string().describe('When (ISO 8601).'),
    scope: AgentModeScopeSchema.describe(
      'The org-wide setting, one repo override, or one machine (a mode switch only; a pin is org or repo).',
    ),
    scope_ref: z
      .string()
      .describe(
        'The referent within `scope`: the account id (a pin at org), the repo id, or the machine id (`runners.id`, a mode switch at machine scope).',
      ),
    purpose: z.string().nullable().describe('The routing purpose, on a pin event; else null.'),
    mode_key: z
      .enum(AGENT_MODE_KEYS as unknown as [AgentModeKey, ...AgentModeKey[]])
      .nullable()
      .describe('The mode, on a mode event; else null.'),
    before: PinWireSchema.nullable().describe(
      'The pin this change replaced; null when it created the pin, on a mode event, or on a row written before change tracking.',
    ),
    after: PinWireSchema.nullable().describe(
      'The pin this change left; null on a clear, a mode event, or a row written before change tracking.',
    ),
    enabled: z
      .boolean()
      .nullable()
      .describe(
        'The switch a mode set stored; null otherwise. A mode’s tool lists are never exposed here.',
      ),
    via: CredentialViaSchema.nullable().describe(
      '`session` for a dashboard sign-in, `pat` for any other credential; null on a row written before change tracking.',
    ),
    actor: z
      .object({
        user_id: z.string(),
        login: z.string().nullable(),
        avatar_url: z.string().nullable(),
      })
      .strict()
      .nullable()
      .describe('The member who made the change; null for a credential with no member behind it.'),
    token_id: z
      .string()
      .nullable()
      .describe(
        'The credential that made the change (an id, never the secret); null on older rows.',
      ),
  })
  .strict();

export type AgentHistoryEventWire = z.infer<typeof AgentHistoryEventWireSchema>;

/** One timeline entry, as `GET /v1/agents/history` pages it (`Page<AgentHistoryEvent>`). */
export const AgentHistoryEventResource = defineResource<
  AgentHistoryEntry,
  typeof AgentHistoryEventWireSchema
>({
  schema: AgentHistoryEventWireSchema,
  serialize: (e) => ({
    id: e.id,
    event: e.event,
    at_iso: e.at.toISOString(),
    scope: e.scope,
    scope_ref: e.scopeRef,
    purpose: e.purpose,
    mode_key: e.modeKey,
    before: e.before,
    after: e.after,
    enabled: e.enabled,
    via: e.via,
    actor:
      e.actor === null
        ? null
        : { user_id: e.actor.userId, login: e.actor.login, avatar_url: e.actor.avatarUrl },
    token_id: e.tokenId,
  }),
});
